import './styles/tokens.css';
import './styles/base.css';
import './styles/app.css';
import './lib/polyfills';

import { Nav } from './ui/Nav';
import { Hero } from './ui/Hero';
import { DetectionTable } from './ui/DetectionTable';
import { PreviewFrame } from './ui/PreviewFrame';
import { TrustSection } from './ui/TrustSection';
import { Pricing } from './ui/Pricing';
import { FAQ } from './ui/FAQ';
import { Footer } from './ui/Footer';
import { BlogIndex } from './ui/BlogIndex';
import { BlogPost as BlogPostView } from './ui/BlogPost';
import { WhyPage } from './ui/WhyPage';
import { LegalPage } from './ui/LegalPage';
import { LivePreviewPanel, type LivePreviewPanel as LivePreviewPanelHandle } from './ui/LivePreviewPanel';
import type { SectionKind } from './ui/ReviewSidebar';
import type { ReviewSelection } from './ui/types';
import { onRouteChange } from './ui/router';
import type { Route } from './ui/routes';

import { dispatch, type DispatchValue } from './pipeline/dispatch';
import { downloadBlob } from './lib/download';
import { redactDispatch } from './lib/redact';
import { detectWithNer, loadNer } from './detect/pii/ner';
import { mergeDetections, augmentWithNer } from './detect/pii/merge';
import { detectWithRegex } from './detect/pii/regex';
import { detectFormFields } from './detect/pii/forms';
import { detectNames as detectNamesFromText } from './detect/pii/names';
import { liftToImageTextHits } from './pipeline/image';
import { readPrefs, writePrefs } from './lib/modelCache';
import type { RedactionStore } from './lib/redactionStore';
import { BatchPanel } from './ui/BatchPanel';
import { createBatch, dispatchAll, redactAndZip } from './lib/batch';
import { LimitModal, type LimitReason } from './ui/LimitModal';
import { LicenseModal } from './ui/LicenseModal';
import { toast } from './ui/Toast';
import {
  FREE_BATCH_FILES,
  FREE_FILE_BYTES,
  PRO_FILE_BYTES,
  canRedactNow,
  recordRedaction,
} from './lib/freeTier';
import { isPro } from './lib/license';

const allowedSectionsFor = (route: Route): ReadonlySet<SectionKind> | undefined =>
  route.allowedSections;

const noticeFor = (value: DispatchValue): string | undefined => {
  if (value.kind === 'image' && value.convertedFromHeic) {
    return 'converted from HEIC to JPEG before processing';
  }
  return undefined;
};

const handleConfirm = async (value: DispatchValue, selection: ReviewSelection): Promise<void> => {
  try {
    const out = await redactDispatch(value, selection);
    await downloadBlob(out.blob, out.filename);
    if (!isPro()) recordRedaction(1);
  } catch (e) {
    toast(`redaction failed: ${e instanceof Error ? e.message : String(e)}`, 'error', 10000);
    throw e;
  }
};

const showLimit = (mount: HTMLElement, reason: LimitReason, limitMb?: number): void => {
  const modal = LimitModal({
    reason,
    ...(limitMb !== undefined && { limitMb }),
    onEnterLicense: () => {
      const lic = LicenseModal({
        onActivated: () => {
          /* license stored — next drop will pass the gate */
        },
        onClose: () => undefined,
      });
      mount.append(lic);
    },
    onClose: () => undefined,
  });
  mount.append(modal);
};

const dropZoneMeta = (): string => {
  const limitMb = (isPro() ? PRO_FILE_BYTES : FREE_FILE_BYTES) / 1024 / 1024;
  return `PDF · PNG · JPG · HEIC — up to ${String(limitMb)} MB`;
};

const checkGates = (mount: HTMLElement, files: readonly File[]): boolean => {
  const pro = isPro();
  const sizeLimit = pro ? PRO_FILE_BYTES : FREE_FILE_BYTES;
  for (const f of files) {
    if (f.size > sizeLimit) {
      showLimit(mount, 'file-size', Math.round(sizeLimit / 1024 / 1024));
      return false;
    }
  }
  if (!pro && files.length > FREE_BATCH_FILES) {
    showLimit(mount, 'batch-size');
    return false;
  }
  if (!pro && !canRedactNow(files.length)) {
    showLimit(mount, 'lifetime');
    return false;
  }
  return true;
};

/**
 * Re-run the detection pipeline with the NER model, returning a new
 * DispatchValue whose detections include free-form names and addresses
 * the regex/forms/dictionary pass missed. Used by the background
 * enrichment pass after the live preview is already mounted.
 */
const runNerOn = async (value: DispatchValue): Promise<DispatchValue> => {
  if (value.kind === 'pdf') {
    const updatedPages = [];
    for (const page of value.pages) {
      const ner = await detectWithNer(page.text);
      // Only ADD NER hits in regions the wave-1 pass missed entirely.
      // Replacing or splitting an existing detection produces visibly
      // worse output (the user sees clean wide bars get fragmented
      // 10-15s later).
      const merged = augmentWithNer(page.detections, ner);
      updatedPages.push({ ...page, detections: merged });
    }
    return { ...value, pages: updatedPages };
  }
  if (value.kind === 'text' || value.kind === 'csv') {
    const ner = await detectWithNer(value.originalText);
    const merged = augmentWithNer(value.detections, ner);
    return { ...value, detections: merged };
  }
  // Image: NER runs on the OCR'd text. Without NER, dispatch only
  // catches names that are in our dictionary or matchable by regex —
  // licenses and passports often have name variants we miss. Run the
  // wave-1 detectors again on the OCR text (so the additive merge has
  // a complete "existing" baseline), then layer in only NER hits that
  // don't overlap an existing detection.
  if (value.ocrText && value.ocrWords) {
    const text = value.ocrText;
    const [namesDets, ner] = await Promise.all([
      detectNamesFromText(text),
      detectWithNer(text),
    ]);
    const wave1 = mergeDetections([
      ...detectWithRegex(text),
      ...detectFormFields(text),
      ...namesDets,
    ]);
    const merged = augmentWithNer(wave1, ner);
    // Word bboxes are in OCR-canvas pixel space, which may be smaller
    // than the bitmap when we downscaled large photos for OCR. Use
    // the saved canvas dims so the normalised rects line up correctly.
    const w = value.ocrCanvasWidth ?? value.bitmap.width;
    const h = value.ocrCanvasHeight ?? value.bitmap.height;
    const newHits = liftToImageTextHits(value.ocrWords, w, h, merged);
    return { ...value, imageTextHits: newHits };
  }
  return value;
};

/**
 * Kick off the NER enrichment pass in the background. Doesn't block the
 * dispatch flow — the panel is mounted with regex/forms/names hits
 * first, then the model loads (one-time, cached) and runs inference,
 * and the new detections are merged into the live store via
 * `replaceFromValue`. The sidebar surfaces a small "loading enhanced
 * detection…" banner while the model is loading.
 *
 * Honors the legacy `prefs.ner === 'skipped'` opt-out from earlier
 * versions of the modal-based UI; for everyone else this runs by
 * default. Skips entirely on inputs with no usable text (face-only
 * photos, etc.).
 */
const enrichWithNerInBackground = (value: DispatchValue, store: RedactionStore): void => {
  if (value.kind === 'image' && (!value.ocrText || value.ocrText.length < 4)) return;
  if (readPrefs().ner === 'skipped') return;

  store.setNerStatus('loading');
  void (async (): Promise<void> => {
    try {
      await loadNer();
      const enriched = await runNerOn(value);
      // The store tracks user toggles + manual entries — replaceFromValue
      // merges in the new AI detections without clobbering either.
      store.replaceFromValue(enriched);
      store.setNerStatus('ready');
      writePrefs({ ner: 'enabled' });
    } catch (err) {
      console.warn('[anomia] NER enrichment failed', err);
      store.setNerStatus('error');
    }
  })();
};

const handleBatch = async (mount: HTMLElement, files: readonly File[]): Promise<void> => {
  document.getElementById('review-panel')?.remove();
  document.getElementById('batch-panel')?.remove();

  const entries = createBatch(files);
  const panel = BatchPanel({
    entries,
    onConfirmAll: () => {
      void (async (): Promise<void> => {
        const result = await redactAndZip(entries, (e) => {
          panel.update(e);
        });
        await downloadBlob(result.blob, result.outputName);
        if (!isPro()) recordRedaction(result.redactedCount);
        panel.root.remove();
      })();
    },
    onCancel: () => {
      panel.root.remove();
    },
  });
  mount.querySelector('.hero')?.after(panel.root);
  panel.root.scrollIntoView({ behavior: 'smooth', block: 'start' });

  await dispatchAll(entries, (e) => {
    panel.update(e);
  });
};

const errorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message || e.name;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
};

const handleFiles = async (
  mount: HTMLElement,
  files: File[],
  route: Route,
): Promise<void> => {
  if (!checkGates(mount, files)) return;
  if (files.length > 1) {
    await handleBatch(mount, files);
    return;
  }
  const file = files[0];
  if (!file) return;

  type ToastCtl = { update(s: string): void; dismiss(): void };
  // Two slots: a generic "still working" toast that fires after 4s if
  // dispatch is taking a while, and an OCR-specific toast for live
  // recognition progress. They're mutually exclusive — once OCR
  // starts, the generic toast is dismissed because OCR has more
  // specific feedback. Both get torn down on dispatch completion or
  // failure.
  const slowSlot: { current: ToastCtl | null } = { current: null };
  const ocrSlot: { current: ToastCtl | null } = { current: null };
  const slowTimer = window.setTimeout(() => {
    if (ocrSlot.current) return; // OCR feedback already covers this
    slowSlot.current = toast(`still working on ${file.name}…`, 'info', 0);
  }, 4000);
  const dismissProgressToasts = (): void => {
    window.clearTimeout(slowTimer);
    slowSlot.current?.dismiss();
    slowSlot.current = null;
    ocrSlot.current?.dismiss();
    ocrSlot.current = null;
  };
  let result;
  try {
    result = await dispatch(file, {
      onProgress: (p) => {
        if (p.kind === 'ocr-start') {
          slowSlot.current?.dismiss();
          slowSlot.current = null;
          ocrSlot.current = toast(p.message, 'info', 0);
        } else if (p.kind === 'ocr-progress') {
          ocrSlot.current?.update(p.message);
        } else {
          ocrSlot.current?.dismiss();
          ocrSlot.current = null;
        }
      },
    });
  } catch (e) {
    dismissProgressToasts();
    toast(`couldn't process ${file.name}: ${errorMessage(e)}`, 'error', 0);
    return;
  }
  dismissProgressToasts();
  if (!result.ok) {
    toast(result.error.message, 'error', 0);
    return;
  }
  const value = result.value;

  document.getElementById('review-panel')?.remove();
  document.getElementById('batch-panel')?.remove();

  const allowed = allowedSectionsFor(route);
  const notice = noticeFor(value);
  const geoFromValue = value.kind === 'image' ? value.geo : undefined;
  let live: LivePreviewPanelHandle;
  // eslint-disable-next-line prefer-const -- assigned in onConfirm via closure
  live = LivePreviewPanel({
    value,
    filename: value.filename,
    outputFilename: value.outputFilename,
    ...(notice !== undefined && { notice }),
    ...(allowed !== undefined && { allowedSections: allowed }),
    ...(geoFromValue !== undefined && { geo: geoFromValue }),
    onConfirm: (selection: ReviewSelection): void => {
      // Read value from the live store so post-NER enrichment is
      // included — the closure-captured `value` is the pre-NER copy.
      void handleConfirm(live.store.value, selection).then(() => {
        live.dispose();
        live.root.remove();
      });
    },
    onCancel: (): void => {
      live.dispose();
      live.root.remove();
    },
    requirePro: (): boolean => {
      if (isPro()) return true;
      showLimit(mount, 'manual-pro');
      return false;
    },
  });

  mount.querySelector('.hero')?.after(live.root);
  live.root.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Kick off the NER enrichment pass after the panel is mounted. It
  // streams new detections into the live store as they're found and
  // surfaces a loading banner via store.nerStatus along the way.
  enrichWithNerInBackground(value, live.store);
};

const renderRoute = (mount: HTMLElement, route: Route): void => {
  document.title = route.title;

  const fragment = document.createDocumentFragment();

  if (route.kind === 'blog-post' && route.blogPost) {
    fragment.append(Nav(), BlogPostView({ post: route.blogPost }), Footer());
    mount.replaceChildren(fragment);
    window.scrollTo({ top: 0 });
    return;
  }

  if (route.kind === 'blog-index') {
    fragment.append(
      Nav(),
      BlogIndex({ heroH1: route.heroH1, heroLede: route.heroLede }),
      Footer(),
    );
    mount.replaceChildren(fragment);
    window.scrollTo({ top: 0 });
    return;
  }

  if (route.kind === 'why') {
    fragment.append(
      Nav(),
      WhyPage({ heroH1: route.heroH1, heroLede: route.heroLede }),
      Footer(),
    );
    mount.replaceChildren(fragment);
    window.scrollTo({ top: 0 });
    return;
  }

  if (route.kind === 'legal' && route.legal) {
    fragment.append(
      Nav(),
      LegalPage({
        heroH1: route.heroH1,
        heroLede: route.heroLede,
        lastUpdated: route.legal.lastUpdated,
        sections: route.legal.sections,
      }),
      Footer(),
    );
    mount.replaceChildren(fragment);
    window.scrollTo({ top: 0 });
    return;
  }

  const onFiles = (files: File[]): void => {
    void handleFiles(mount, files, route);
  };

  fragment.append(
    Nav(),
    Hero({
      headline: route.heroH1,
      lede: route.heroLede,
      dropZone: {
        onFiles,
        meta: dropZoneMeta(),
      },
    }),
    DetectionTable(route.detectionRows ? { rows: route.detectionRows } : {}),
    PreviewFrame(),
    TrustSection(),
    Pricing(),
    FAQ(),
    Footer(),
  );

  mount.replaceChildren(fragment);
};

const installGlobalErrorReporting = (): void => {
  window.addEventListener('error', (e) => {
    toast(`error: ${e.message}`, 'error', 12000);
  });
  window.addEventListener('unhandledrejection', (e) => {
    toast(`error: ${errorMessage(e.reason)}`, 'error', 12000);
  });
};

const init = (): void => {
  installGlobalErrorReporting();
  const mount = document.getElementById('app');
  if (!mount) throw new Error('#app mount node missing from index.html');
  onRouteChange((route) => {
    renderRoute(mount, route);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
